import { useState, useEffect, useCallback, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Users, MessageCircle, Heart, Share2, Plus, 
  Globe, Sparkles
} from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "All Posts" },
  { id: "support", label: "Support" },
  { id: "inspiration", label: "Inspiration" },
  { id: "discussion", label: "Discussion" },
  { id: "resources", label: "Resources" },
];

const CATEGORY_COLORS = {
  support: 'bg-blue-100 text-blue-700',
  inspiration: 'bg-green-100 text-green-700',
  resources: 'bg-purple-100 text-purple-700',
  discussion: 'bg-spa-muted text-spa-text-secondary'
};

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({ title: "", content: "", category: "discussion" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const url = activeCategory === "all" 
        ? '/community/posts'
        : `/community/posts?category=${activeCategory}`;
      const response = await apiClient.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    }
  }, [activeCategory]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) return;
    
    setIsSubmitting(true);
    try {
      await apiClient.post('/community/posts', newPost);
      setNewPost({ title: "", content: "", category: "discussion" });
      setShowCreatePost(false);
      fetchPosts();
    } catch (error) {
      // silent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      await apiClient.post(`/community/posts/${postId}/like`);
      fetchPosts();
    } catch (error) {
      // silent
    }
  };

  const postCategories = useMemo(() => 
    CATEGORIES.filter(c => c.id !== 'all'), 
    []
  );

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="community-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="overline-text mb-2">Global Social.VILLAGE</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
              Community Forum
            </h1>
            <p className="text-spa-text-secondary max-w-2xl">
              Connect with fellow wellness seekers, share your journey, and support 
              each other in the SMAHOO Global Social.VILLAGE community.
            </p>
          </div>
          <Button 
            onClick={() => setShowCreatePost(true)}
            className="spa-button-primary flex items-center gap-2"
            data-testid="create-post-button"
          >
            <Plus className="w-4 h-4" /> New Post
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="spa-card py-4 text-center">
            <Users className="w-6 h-6 text-spa-sage mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">5,000+</p>
            <p className="text-xs text-spa-text-secondary">Social Network Followers</p>
          </div>
          <div className="spa-card py-4 text-center">
            <Globe className="w-6 h-6 text-spa-sky mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">WHO</p>
            <p className="text-xs text-spa-text-secondary">Aligned</p>
          </div>
          <div className="spa-card py-4 text-center">
            <MessageCircle className="w-6 h-6 text-spa-terracotta mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">{posts.length}</p>
            <p className="text-xs text-spa-text-secondary">Discussions</p>
          </div>
          <div className="spa-card py-4 text-center">
            <Sparkles className="w-6 h-6 text-spa-clay mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">24/7</p>
            <p className="text-xs text-spa-text-secondary">Support</p>
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-spa-terracotta text-white'
                  : 'bg-spa-muted text-spa-text-secondary hover:bg-spa-border'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Create Post Modal */}
        {showCreatePost && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg animate-fade-in" data-testid="create-post-modal">
              <h2 className="font-serif text-2xl text-spa-moss mb-6">Create Post</h2>
              
              <div className="space-y-4">
                <Input
                  placeholder="Post title"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  className="spa-input"
                  data-testid="post-title-input"
                />
                
                <Textarea
                  placeholder="Share your thoughts, ask for support, or inspire others..."
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  className="spa-input min-h-[120px]"
                  data-testid="post-content-input"
                />

                <div>
                  <label className="text-sm text-spa-text-secondary mb-2 block">Category</label>
                  <div className="flex flex-wrap gap-2">
                    {postCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setNewPost({ ...newPost, category: cat.id })}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          newPost.category === cat.id
                            ? 'bg-spa-terracotta text-white'
                            : 'bg-spa-muted text-spa-text-secondary hover:bg-spa-border'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreatePost(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreatePost}
                  disabled={isSubmitting}
                  className="flex-1 spa-button-primary"
                  data-testid="submit-post-button"
                >
                  {isSubmitting ? "Posting..." : "Post (+10 Coins)"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Posts Feed */}
        <div className="space-y-4">
          {posts.map((post) => (
            <div 
              key={post.post_id}
              className="spa-card"
              data-testid={`post-${post.post_id}`}
            >
              <div className="flex items-start gap-4">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={post.user_picture} />
                  <AvatarFallback className="bg-spa-sage text-white font-serif">
                    {post.user_name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-spa-moss">{post.user_name}</span>
                    <span className="text-xs text-spa-text-secondary">
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      CATEGORY_COLORS[post.category] || CATEGORY_COLORS.discussion
                    }`}>
                      {post.category}
                    </span>
                  </div>
                  <h3 className="font-serif text-lg text-spa-moss mb-2">{post.title}</h3>
                  <p className="text-spa-text-secondary">{post.content}</p>
                  
                  <div className="flex items-center gap-6 mt-4 pt-4 border-t border-spa-border">
                    <button 
                      onClick={() => handleLike(post.post_id)}
                      className="flex items-center gap-2 text-spa-text-secondary hover:text-spa-terracotta transition-colors"
                      data-testid={`like-${post.post_id}`}
                    >
                      <Heart className="w-4 h-4" />
                      <span className="text-sm">{post.likes || 0}</span>
                    </button>
                    <button className="flex items-center gap-2 text-spa-text-secondary hover:text-spa-moss transition-colors">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">{post.comments_count || 0}</span>
                    </button>
                    <button className="flex items-center gap-2 text-spa-text-secondary hover:text-spa-moss transition-colors ml-auto">
                      <Share2 className="w-4 h-4" />
                      <span className="text-sm">Share</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {posts.length === 0 && (
            <div className="spa-card text-center py-12">
              <Users className="w-12 h-12 text-spa-text-secondary mx-auto mb-4" />
              <h3 className="font-serif text-xl text-spa-moss mb-2">No posts yet</h3>
              <p className="text-spa-text-secondary mb-4">Be the first to share in the community!</p>
              <Button 
                onClick={() => setShowCreatePost(true)}
                className="spa-button-primary"
              >
                Create First Post
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
